import React, { useState, useEffect } from 'react';
import { CookieConsent as CookieConsentUtil } from '../../utils/security';

const CookieConsent: React.FC = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Check if user has already given consent
    const hasConsent = CookieConsentUtil.hasConsent();
    setShowBanner(!hasConsent);
  }, []);

  const handleAccept = async () => {
    setIsLoading(true);
    try {
      CookieConsentUtil.recordConsent();
      setShowBanner(false);
    } catch (error) {
      console.error('Error recording consent:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDecline = () => {
    // For now, we'll hide the banner but not set consent
    // In a real implementation, you might want to disable certain features
    setShowBanner(false);
  };

  if (!showBanner) {
    return null;
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-gray-900 text-white p-4 shadow-lg z-50">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex-1">
            <h3 className="text-lg font-semibold mb-2">Cookie & Privacy Notice</h3>
            <p className="text-sm text-gray-300">
              We use necessary cookies to ensure our survey platform functions properly and to protect your data.
              We also use analytics cookies to improve our services. Your survey responses are always anonymous
              and encrypted. By using this site, you consent to our use of cookies as described in our{' '}
              <a href="/privacy" className="text-blue-400 hover:text-blue-300 underline">
                Privacy Policy
              </a>.
            </p>
            <div className="mt-2">
              <details className="text-xs text-gray-400">
                <summary className="cursor-pointer hover:text-gray-300">
                  What cookies do we use?
                </summary>
                <div className="mt-2 space-y-1">
                  <p><strong>Essential:</strong> Session management, security, form data protection</p>
                  <p><strong>Analytics:</strong> Anonymous usage statistics to improve the platform</p>
                  <p><strong>Functional:</strong> Save your progress on surveys, remember your preferences</p>
                  <p>We do not use advertising or tracking cookies.</p>
                </div>
              </details>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
            <button
              onClick={handleDecline}
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-gray-300 bg-transparent border border-gray-600 rounded-md hover:bg-gray-800 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Essential Only
            </button>
            <button
              onClick={handleAccept}
              disabled={isLoading}
              className="px-6 py-2 text-sm font-medium text-white bg-primary border border-transparent rounded-md hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Accepting...
                </>
              ) : (
                'Accept All Cookies'
              )}
            </button>
          </div>
        </div>

        {/* Compliance Information */}
        <div className="mt-4 pt-3 border-t border-gray-700">
          <p className="text-xs text-gray-400 text-center">
            🔒 Your privacy is protected. Survey data is encrypted, anonymized, and stored securely.
            This platform complies with healthcare privacy regulations and GDPR requirements.
          </p>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;